import React, { useEffect, useState, useCallback } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import {
  GET_MY_CLASS,
  ENSURE_CLASS_CODE,
  REGENERATE_CLASS_CODE,
  REMOVE_CLASS_STUDENT,
} from '../graphql/class-queries';

// ClassPanel — teacher Settings card. Surfaces the class code + roster and
// lets the teacher copy / rotate the code or remove individual students.
function ClassPanel({ teacher }) {
  const { data, loading, error, refetch } = useQuery(GET_MY_CLASS, {
    fetchPolicy: 'cache-and-network',
  });
  const [ensureCode, ensureState] = useMutation(ENSURE_CLASS_CODE, {
    refetchQueries: [{ query: GET_MY_CLASS }],
  });
  const [regenerateCode, regenState] = useMutation(REGENERATE_CLASS_CODE, {
    refetchQueries: [{ query: GET_MY_CLASS }],
  });
  const [removeStudent, removeState] = useMutation(REMOVE_CLASS_STUDENT, {
    refetchQueries: [{ query: GET_MY_CLASS }],
  });

  const [copied, setCopied] = useState(false);

  // Auto-create a code on first visit so the teacher always has one to share.
  useEffect(() => {
    if (loading) return;
    if (error) return;
    if (data?.myClass) return;
    ensureCode().catch(() => { /* surface via mutation error state */ });
  }, [loading, data, error, ensureCode]);

  const cls = data?.myClass;
  const limit = cls?.studentLimit ?? (teacher?.plan === 'pro' ? 40 : 4);
  const enrolled = cls?.students?.length ?? 0;
  const isFree = teacher?.plan !== 'pro';
  const isFull = enrolled >= limit;

  const joinUrl = cls?.code
    ? `${window.location.origin}/class/${cls.code}`
    : '';

  const handleCopy = useCallback(async () => {
    if (!joinUrl) return;
    try {
      await navigator.clipboard.writeText(joinUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // ignore — user can copy manually
    }
  }, [joinUrl]);

  const handleRegenerate = useCallback(() => {
    const ok = window.confirm(
      'Rotate the class code? This will sign out every current student and clear the roster. They\'ll need the new code to rejoin.'
    );
    if (!ok) return;
    regenerateCode();
  }, [regenerateCode]);

  const handleRemove = useCallback((name) => {
    const ok = window.confirm(`Remove ${name} from your class?`);
    if (!ok) return;
    removeStudent({ variables: { name } });
  }, [removeStudent]);

  if (loading && !cls) {
    return (
      <section className="card" style={{ padding: 28, marginBottom: 24 }}>
        <h2 className="t-h3" style={{ marginBottom: 8 }}>Class</h2>
        <p className="t-small">Loading…</p>
      </section>
    );
  }

  return (
    <section className="card" style={{ padding: 28, marginBottom: 24 }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'baseline',
        marginBottom: 8,
      }}>
        <h2 className="t-h3">Class · 班级</h2>
        <span className="t-small">
          {enrolled} / {limit} students
        </span>
      </div>
      <p className="t-small" style={{ marginBottom: 20 }}>
        Share your class code with students so they can study your library on their own devices.
      </p>

      {cls?.code && (
        <>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            background: 'var(--paper-2)',
            border: '1px solid var(--rule)',
            borderRadius: 10,
            padding: '14px 18px',
            marginBottom: 12,
          }}>
            <div style={{ flex: 1 }}>
              <div className="t-eyebrow" style={{ marginBottom: 4 }}>Code</div>
              <div className="t-mono" style={{
                fontSize: 28,
                letterSpacing: '0.16em',
                fontWeight: 500,
              }}>
                {cls.code}
              </div>
            </div>
            <button className="btn btn--sm" onClick={handleCopy} disabled={!joinUrl}>
              {copied ? 'Copied!' : 'Copy link'}
            </button>
          </div>
          <p className="t-small" style={{ marginBottom: 20, wordBreak: 'break-all' }}>
            {joinUrl}
          </p>

          <div style={{ display: 'flex', gap: 8, marginBottom: 24 }}>
            <button
              className="btn btn--ghost btn--sm"
              onClick={handleRegenerate}
              disabled={regenState.loading}
            >
              {regenState.loading ? 'Rotating…' : 'Rotate code'}
            </button>
          </div>
        </>
      )}

      {ensureState.error && (
        <p style={{ color: 'var(--error)', fontSize: 13, marginBottom: 12 }}>
          Could not create class code: {ensureState.error.message}
        </p>
      )}
      {regenState.error && (
        <p style={{ color: 'var(--error)', fontSize: 13, marginBottom: 12 }}>
          Could not rotate code: {regenState.error.message}
        </p>
      )}
      {removeState.error && (
        <p style={{ color: 'var(--error)', fontSize: 13, marginBottom: 12 }}>
          Could not remove student: {removeState.error.message}
        </p>
      )}

      <h3 className="t-h3" style={{ fontSize: 16, marginBottom: 12 }}>Roster</h3>
      {enrolled === 0 ? (
        <p className="t-small">No students yet — share the link above.</p>
      ) : (
        <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
          {cls.students.map(s => (
            <li
              key={s.name}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '10px 0',
                borderBottom: '1px solid var(--rule)',
              }}
            >
              <span>{s.name}</span>
              <button
                className="btn btn--ghost btn--sm"
                onClick={() => handleRemove(s.name)}
                aria-label={`Remove ${s.name}`}
              >
                ✕
              </button>
            </li>
          ))}
        </ul>
      )}

      {isFree && isFull && (
        <p style={{
          marginTop: 20,
          padding: '12px 14px',
          background: 'var(--paper-2)',
          border: '1px solid var(--rule)',
          borderRadius: 8,
          fontSize: 13,
        }}>
          Your free plan supports up to {limit} students. Upgrade to Pro to enrol up to 40.
        </p>
      )}

      {error && (
        <p style={{ color: 'var(--error)', fontSize: 13, marginTop: 12 }}>
          Could not load class info. <button className="btn btn--ghost btn--sm" onClick={() => refetch()}>Retry</button>
        </p>
      )}
    </section>
  );
}

export default ClassPanel;
